import React from "react"
import { Switch } from "antd"
import { DataTile, isDataTile } from "features/Dashboard/types/dashboardDocument"
import { useDashboardStore } from "features/Dashboard/runtime/dashboardStore"
import { repeatOf } from "features/Dashboard/runtime/panels"

const SCRIPT_SETTINGS_HINT =
  "Script tiles have no builder settings to change."

/**
 * What viewers may change on this tile in View mode (the controls in its
 * header) and how it takes part in cross-filtering.
 */
const ControlsTab: React.FC<{ tile: DataTile; pageId: string }> = ({
  tile,
  pageId,
}) => {
  const updateTile = useDashboardStore((s) => s.updateTile)
  const setControls = (patch: Partial<DataTile["controls"]>): void =>
    updateTile(pageId, tile.id, (t) =>
      isDataTile(t) ? { ...t, controls: { ...t.controls, ...patch } } : t
    )
  const setCross = (patch: Partial<DataTile["crossFilter"]>): void =>
    updateTile(pageId, tile.id, (t) =>
      isDataTile(t) ? { ...t, crossFilter: { ...t.crossFilter, ...patch } } : t
    )
  const script = tile.source.kind === "script"
  // The categories control only appears for a single row variable.
  const oneRow = tile.source.kind === "builder" && tile.source.rows.length === 1

  const row = (
    label: string,
    checked: boolean,
    onChange: (v: boolean) => void,
    disabled = false,
    hint?: string
  ) => (
    <div className="dq-toggle">
      <label className="dq-field dq-field--row">
        <span>{label}</span>
        <Switch
          size="small"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
        />
      </label>
      {hint && <p className="dq-field__hint">{hint}</p>}
    </div>
  )

  // A script tile never shows viewer controls or emits, whatever is stored.
  return (
    <div className="dq-inspector__section">
      <h4 className="dq-inspector__h">Viewers can change</h4>
      {script && <p className="dq-field__hint">{SCRIPT_SETTINGS_HINT}</p>}
      {!script && repeatOf(tile) && (
        <p className="dq-field__hint">
          Hidden while the tile repeats by a variable, so every panel shows
          the same table.
        </p>
      )}
      {row(
        "Statistic",
        tile.controls.statistic && !script,
        (v) => setControls({ statistic: v }),
        script
      )}
      {row(
        "Breakdown",
        tile.controls.breakdown && !script,
        (v) => setControls({ breakdown: v }),
        script
      )}
      {row(
        "Categories",
        tile.controls.categories && !script,
        (v) => setControls({ categories: v }),
        script,
        !script && !oneRow
          ? "Shown to viewers when the tile has one row variable."
          : undefined
      )}
      <h4 className="dq-inspector__h">Cross-filtering</h4>
      {row(
        "Clicks filter other tiles",
        tile.crossFilter.emits && !script,
        (v) => setCross({ emits: v }),
        script,
        script
          ? "Script tiles cannot emit: the runtime cannot map their labels to variables."
          : undefined
      )}
      {row(
        "Filtered by other tiles",
        tile.crossFilter.receives,
        (v) => setCross({ receives: v })
      )}
    </div>
  )
}

export default ControlsTab
